import {
  Database,
  Network,
  ClipboardList,
  FileBarChart,
  Lock,
  Lightbulb,
  Compass,
  CheckCircle2,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import { PrimaryCTA, SecondaryCTA } from "@/components/CTAButtons";

const CAPABILITIES = [
  {
    icon: Database,
    title: "Organizes buyer and supplier intelligence",
    body:
      "A private knowledge base built on years of relationships, conference notes, and verified counterparties.",
  },
  {
    icon: Network,
    title: "Preserves conference and market knowledge",
    body:
      "Every show, every meeting, every market signal — captured so insight doesn't walk out the door.",
  },
  {
    icon: Compass,
    title: "Supports sourcing analysis and match evaluation",
    body:
      "Analysts use the engine to compare suppliers against buyer requirements with structured rigour.",
  },
  {
    icon: ClipboardList,
    title: "Generates tailored reports and opportunity briefs",
    body:
      "Curated outputs are produced for each engagement — never templated, never generic.",
  },
];

const DELIVERABLES = [
  {
    label: "Sourcing intelligence reports",
    body:
      "Written briefs covering market structure, pricing context, and substitution risk for your category.",
  },
  {
    label: "Supplier shortlists",
    body:
      "Verified counterparties matched against your specs, geography, certifications, and volume profile.",
  },
  {
    label: "Buyer opportunity briefs",
    body:
      "For qualified suppliers — written introductions to active buyer programs in target markets.",
  },
  {
    label: "Verification-backed recommendations",
    body:
      "Conference-grade, relationship-checked suggestions you can put in front of leadership.",
  },
  {
    label: "Warm introductions",
    body:
      "FFIB hosts the meeting where useful — and stays available to support follow-through.",
  },
];

export default function Platform() {
  return (
    <div data-testid="platform-page">
      <PageHero
        eyebrow="The Platform"
        title={
          <>
            A private intelligence engine
            <br />
            for <span className="text-brand-olive">better sourcing decisions</span>.
          </>
        }
        subtitle="The IngredientSignal platform is internal to FFIB. It powers our analysts so that you, the client, receive sharper reports, better-fit shortlists, and more relevant introductions — without ever touching the software."
        accent={
          <>
            <span className="block text-brand-deep font-medium mb-2">
              Internal-only software
            </span>
            Clients don't subscribe, log in, or train on tools. They receive
            deliverables.
          </>
        }
      >
        <div className="flex flex-wrap gap-4">
          <PrimaryCTA to="/get-started" testId="platform-hero-cta">
            Request a Tailored Report
          </PrimaryCTA>
          <SecondaryCTA to="/case-studies" testId="platform-hero-secondary">
            See outcomes
          </SecondaryCTA>
        </div>
      </PageHero>

      {/* Clarification block */}
      <section className="py-16 border-b border-brand-border">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-start gap-5 border-l-2 border-brand-olive pl-6">
            <Lock
              className="w-5 h-5 text-brand-olive mt-1.5 flex-shrink-0"
              strokeWidth={1.5}
            />
            <p className="text-lg text-brand-warm leading-relaxed font-light">
              No subscription required. No self-serve login. No software
              training. The platform is FFIB's operating system — your
              experience is curated reports, expert recommendations, and warm
              introductions.
            </p>
          </div>
        </div>
      </section>

      {/* What the platform does */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="What it does"
            title="The engine behind every IngredientSignal deliverable."
            intro="The platform exists to make FFIB's analysts faster, sharper, and more accountable on your behalf."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CAPABILITIES.map((cap) => {
              const Icon = cap.icon;
              return (
                <div
                  key={cap.title}
                  data-testid={`platform-capability-${cap.title.toLowerCase().split(" ")[0]}`}
                  className="bg-brand-ivory border border-brand-border p-10 hover:border-brand-muted/40 transition-colors"
                >
                  <Icon
                    className="w-7 h-7 text-brand-olive"
                    strokeWidth={1.4}
                  />
                  <h3 className="font-display mt-6 text-xl text-brand-deep font-medium leading-snug">
                    {cap.title}
                  </h3>
                  <p className="mt-4 text-brand-muted leading-relaxed font-light">
                    {cap.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 lg:py-32 bg-brand-linen border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="How it works"
            title="From inquiry to introduction — guided by analysts."
          />
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                title: "Capture",
                body:
                  "FFIB intakes your need: category, geography, volume, certifications, timeline.",
                Icon: Lightbulb,
              },
              {
                step: "02",
                title: "Analyze",
                body:
                  "Analysts run the request against our private intelligence base and conference network.",
                Icon: FileBarChart,
              },
              {
                step: "03",
                title: "Deliver",
                body:
                  "You receive a written deliverable. Where appropriate, FFIB hosts the introduction.",
                Icon: CheckCircle2,
              },
            ].map((s) => (
              <div
                key={s.step}
                data-testid={`platform-howit-${s.step}`}
                className="bg-brand-cream border border-brand-border p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-brand-olive tracking-[0.2em]">
                    STEP {s.step}
                  </span>
                  <s.Icon
                    className="w-5 h-5 text-brand-quiet"
                    strokeWidth={1.4}
                  />
                </div>
                <h3 className="font-display mt-8 text-2xl text-brand-deep font-light">
                  {s.title}
                </h3>
                <p className="mt-4 text-brand-muted leading-relaxed font-light">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What clients receive */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="Deliverables"
            title="What clients actually receive."
            intro="Tangible outputs your team can act on — written, sourced, and accountable to FFIB analysts."
          />
          <ul className="mt-16 divide-y divide-brand-border border-y border-brand-border">
            {DELIVERABLES.map((d, i) => (
              <li
                key={d.label}
                data-testid={`platform-deliverable-${i}`}
                className="grid grid-cols-12 gap-6 py-8 group hover:bg-brand-linen/70 transition-colors px-4 -mx-4"
              >
                <div className="col-span-12 md:col-span-1 font-mono text-xs text-brand-olive tracking-[0.2em] pt-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-12 md:col-span-4">
                  <h3 className="font-display text-xl text-brand-deep font-medium">
                    {d.label}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="text-brand-muted leading-relaxed font-light">
                    {d.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-brand-border bg-brand-linen/70">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-deep font-light leading-tight">
            Ready to brief FFIB?
          </h2>
          <p className="mt-5 text-brand-muted max-w-2xl font-light">
            Share the sourcing need. We'll return the right deliverable.
          </p>
          <div className="mt-8">
            <PrimaryCTA to="/get-started" testId="platform-bottom-cta">
              Get Started
            </PrimaryCTA>
          </div>
        </div>
      </section>
    </div>
  );
}
